import DataHelper from "@/helper/data-helper"
import { HTTPMethods, HTTPRequestBuilder } from "@/builder/HTTPRequestBuilder"
import { BaseRepository } from "@/repositories/base-repository"
import { ODataQueryBuilder } from "@/builder/odata-query-builder"
import type { PublicationContext, SubscribedContext } from "centrifuge"
import type { IBase } from "@/interfaces/api/base.interface"
import { useAuthStore } from "@/store/auth.store"
import { useStorage } from "@vueuse/core"
import { version } from "../../package.json"
import { applyStartupChanges } from "./startup"
import { resources, notificationSnapshot as snapshot } from "./notification-snapshot"
import { beginPlanningRecovery, finishPlanningRecovery } from "./planning-recovery-gate"
import { recoveryDebug } from "./recovery-debug"

/** Per browser: a superuser can switch the cache off in the sidebar; the server decides whether it exists. */
export const planningCacheEnabled = useStorage("planning-cache-enabled", true)

/** Keeps cache restoration and checkpoints out of the live socket handlers. */
export class SocketRecovery {
  private enabled = false
  private since?: StreamPosition
  private position?: StreamPosition
  private restoring = Promise.resolve()
  private chain = Promise.resolve()
  private changes: ChangeNotifications

  constructor(
    private dispatch: (message: any) => void,
    private liveSettled: () => Promise<void>,
    applyFallback: (model: string, deleted: boolean, data: IBase) => Promise<void>
  ) {
    this.changes = new ChangeNotifications(
      readStartupChanges,
      async (model, changes) => {
        await this.restoring // Fetch while the cache restores; apply only afterwards.
        const startup = changes.map(({ operation, row }) => ({
          row,
          deleted: operation === "model_delete"
        }))
        for (const { row, deleted } of startup) if (deleted) snapshot.remove(model, row.id!)
        if (!(await applyStartupChanges(model, startup)))
          for (const { row, deleted } of startup) await applyFallback(model, deleted, row)
      },
      (error) => {
        console.error("[recovery] Retrieval failed; retrying", error)
        this.release()
      },
      (ids) => snapshot.affectedCollections(ids)
    )
  }

  async open(channel: string, enabled: boolean, retained: boolean) {
    this.enabled = enabled && planningCacheEnabled.value
    enabled = this.enabled
    configureRetainedObjects(enabled && retained)
    if (!enabled) {
      snapshot.disable()
      this.release()
      return undefined
    }
    const user = useAuthStore().activeUser
    this.since = await snapshot.open(
      JSON.stringify([
        "startup-notifications-v2",
        import.meta.env.VITE_API_URL,
        version,
        channel,
        user?.id,
        user?.groups,
        user?.isStaff,
        user?.isSuperuser,
        user?.knownBusinessEntityContact?.roles.map((role) => role.type).sort()
      ])
    )
    this.position = this.since
    if (this.since) recoveryDebug.start(this.since.since)
    // Ask Django before the socket exists; the subscription starts where Django looked, so nothing is missed.
    const recovery = this.recover(this.since)
    if (this.since) {
      beginPlanningRecovery()
      // The request interceptor awaits the auth token before sending; the restore would hold the thread until then.
      this.restoring = new Promise<void>((resolve) => setTimeout(resolve))
        .then(() => snapshot.restoreAtStartup())
        .then(() => recoveryDebug.mark("restored"))
        .then(() => this.release()) // The restored planning shows now; the changes apply on top of it.
    } else this.release()
    this.since = undefined
    this.chain = recovery.then(
      () => undefined,
      (error) => {
        this.invalidate()
        console.error("[recovery] Recovery failed", error)
      }
    )
    void Promise.all([this.restoring]).then(() => snapshot.allowStartupRequests())
    const changes = await recovery.catch(() => undefined)
    return changes?.epoch ? { offset: changes.offset, epoch: changes.epoch } : undefined
  }

  subscribed(ctx: SubscribedContext) {
    if (!this.enabled) return
    if (ctx.wasRecovering && !ctx.recovered) {
      this.invalidate()
      this.release()
    }
    recoveryDebug.mark("subscribed")
    this.checkpoint() // Subscribing paused the snapshot; the position is safe to save once the live feed runs.
    void snapshot.finishRecovery(() => this.settled())
  }

  // Ask Django which objects changed since the saved position; the queue fetches only those.
  private async recover(saved?: StreamPosition) {
    const changes = await readChanges(saved)
    recoveryDebug.mark("changes")
    for (const { model, updated, deleted } of changes.changes) {
      if (!resources[model] && model !== "Instruction") continue
      recoveryDebug.notification({ details: { model, data: { ids: [...updated, ...deleted] } } })
      if (updated.length) this.changes.receive(model, { ids: updated, operation: "model_update" })
      if (deleted.length) this.changes.receive(model, { ids: deleted, operation: "model_delete" })
    }
    this.position = { since: changes.since }
    this.checkpoint()
    return changes
  }

  publication(ctx: PublicationContext) {
    if (!this.enabled) return this.dispatch(ctx.data)
    this.pause()
    this.chain = this.chain
      .then(async () => {
        await this.restoring
        if (ctx.data.type === "model_changed") {
          if (receiveRecoveryChange(this.changes, ctx.data)) return
        } else snapshot.applyLiveMessage(ctx.data)
        this.dispatch(ctx.data)
      })
      .catch((error) => {
        this.invalidate()
        console.error("[recovery] Live message failed", error)
      })
    this.checkpoint()
  }

  private async settled() {
    await this.restoring
    await this.chain
    await this.liveSettled()
    await this.changes.whenIdle()
  }

  private checkpoint() {
    if (this.position) snapshot.checkpoint(this.position, () => this.settled())
  }

  pause() {
    if (this.enabled) snapshot.pause()
  }
  invalidate() {
    snapshot.clear()
  }
  release() {
    finishPlanningRecovery()
  }
}

let retainedObjects = false
export function configureRetainedObjects(enabled: boolean) {
  retainedObjects = enabled
}

export type StreamPosition = { since: string }

type Changes = {
  since: string
  offset: number
  epoch: string
  changes: { model: string; updated: string[]; deleted: string[] }[]
}

/** Without a position Django only returns where to continue from next time. */
export async function readChanges(position?: StreamPosition): Promise<Changes> {
  const request = new HTTPRequestBuilder(HTTPMethods.GET, "core/cache/changes/")
  if (position) request.query({ since: position.since })
  const changes = (await request.build().execute()).data
  return { ...changes, since: changes.since.toISOString() } // The HTTP layer parsed the position into a moment.
}

class StartupRepository extends BaseRepository<IBase> {
  constructor(resource: string) {
    super()
    this.resourcePath = resource
  }
}

export async function readStartupChanges(model: string, ids: string[]) {
  const resource =
    resources[model] ?? (model === "Instruction" ? "planning/instructions" : undefined)
  if (!resource) throw new Error(`Unsupported notification: ${model}`)
  const started = performance.now()
  const query = new ODataQueryBuilder().in("id", ids)
  if (retainedObjects) query.with("retained", true)
  if (model === "WorkPeriodTemplate" || model === "InstructionFeedbackActivityTemplate") {
    const owner = useAuthStore().activeUser?.knownBusinessEntityContact?.id
    if (!owner) return []
    query.eq("owner_id", owner).deep()
  }
  const response = await new StartupRepository(resource).getList({ query })
  const data = response.data
  recoveryDebug.fetched(
    model,
    ids.length,
    data,
    performance.now() - started,
    response.headers?.["x-retained-objects"] ? "map" : "http"
  )
  return data
}

// Only called for publications replayed during startup. Live CRUD keeps its
// original full-payload handlers and never triggers this retrieval queue.
export function receiveRecoveryChange(queue: ChangeNotifications, message: any): boolean {
  const model = message.details.model
  if (!resources[model] && model !== "Instruction") return false
  if (message.type === "model_changed") {
    queue.receive(model, message.details.data)
    return true
  }
  const data = message.details.isBulk
    ? DataHelper.denormalizeData(message.details.data)
    : message.details.data
  const rows = Array.isArray(data) ? data : [data]
  const ids = rows.map((row) => row?.id).filter(Boolean)
  if (!ids.length) return false
  if (message.details.complete && message.type !== "model_delete") {
    // Django marked this payload as the canonical read representation: apply it without a fetch.
    snapshot.record(resources[model] ?? "planning/instructions", rows)
    recoveryDebug.replayed(rows)
    queue.receive(model, {
      ids,
      operation: message.type,
      rows: DataHelper.convertDateStringsToMoment(rows)
    })
    return true
  }
  queue.receive(model, { ids, operation: message.type })
  return true
}

export type ChangeOperation = "model_create" | "model_update" | "model_delete"
export type ChangeNotification = { ids: string[]; operation: ChangeOperation; rows?: IBase[] }
type Change = { operation: ChangeOperation; sequence: number; row?: IBase }
export type AppliedChange = { operation: ChangeOperation; row: IBase }

/** Coalesce notifications; a change received during a fetch always gets another fetch. */
export class ChangeNotifications {
  private pending = new Map<string, Map<string, Change>>()
  private latest = new Map<string, number>()
  private sequence = 0
  private timer?: ReturnType<typeof setTimeout>
  private fetching = false
  private idleWaiters: (() => void)[] = []

  whenIdle(): Promise<void> {
    if (!this.fetching && !this.timer) return Promise.resolve()
    return new Promise((resolve) => this.idleWaiters.push(resolve))
  }

  constructor(
    private read: (model: string, ids: string[]) => Promise<IBase[]>,
    private apply: (model: string, changes: AppliedChange[]) => void | Promise<void>,
    private onError: (error: unknown) => void,
    private findAffectedParents: (ids: Set<string>) => Record<string, string[]> = () => ({})
  ) {}

  receive(model: string, notification: ChangeNotification) {
    let changes = this.pending.get(model)
    if (!changes) this.pending.set(model, (changes = new Map()))
    for (const id of notification.ids) {
      const row = notification.rows?.find((candidate) => candidate.id === id)
      const change = { operation: notification.operation, sequence: ++this.sequence, row }
      this.latest.set(`${model}:${id}`, change.sequence)
      changes.set(id, change)
    }
    this.schedule(100)
  }

  private schedule(delay: number) {
    if (!this.timer && !this.fetching) {
      this.timer = setTimeout(() => void this.flush(), delay)
    }
  }

  private async flush() {
    this.timer = undefined
    this.fetching = true
    let failed = false
    try {
      // Scan cached parents once for the coalesced changes, preserving explicit operations.
      const changedIds = new Set<string>()
      for (const changes of this.pending.values()) {
        for (const id of changes.keys()) changedIds.add(id)
      }
      for (const [model, ids] of Object.entries(this.findAffectedParents(changedIds))) {
        const parents = ids.filter((id) => !this.pending.get(model)?.has(id))
        if (parents.length) this.receive(model, { ids: parents, operation: "model_update" })
      }
      for (const [model, changes] of this.pending) {
        // Fetch all pending IDs for this model. Long filters use the POST fallback.
        const batch = [...changes]
        if (!batch.length) continue
        for (const [id] of batch) changes.delete(id)
        try {
          const ids = batch
            .filter(([, change]) => change.operation !== "model_delete" && !change.row)
            .map(([id]) => id)
          const rows = new Map(
            (ids.length ? await this.read(model, ids) : []).map((row) => [row.id, row])
          )
          const applied: AppliedChange[] = []
          for (const [id, change] of batch) {
            if (this.latest.get(`${model}:${id}`) !== change.sequence) continue
            const row = change.row ?? rows.get(id)
            if (row && change.operation !== "model_delete")
              applied.push({ operation: change.operation, row })
            else applied.push({ operation: "model_delete", row: { id } }) // Deleted since the notification.
          }
          if (applied.length) await this.apply(model, applied) // One pass per model: the stores update once.
          for (const [id, change] of batch) {
            const key = `${model}:${id}`
            if (this.latest.get(key) === change.sequence) this.latest.delete(key)
          }
        } catch (error) {
          failed = true
          for (const [id, change] of batch) {
            if (this.latest.get(`${model}:${id}`) === change.sequence) changes.set(id, change)
          }
          this.onError(error)
        }
      }
    } catch (error) {
      failed = true
      this.onError(error)
    } finally {
      this.fetching = false
      if ([...this.pending.values()].some((changes) => changes.size))
        this.schedule(failed ? 1000 : 0)
      else {
        this.latest.clear()
        for (const resolve of this.idleWaiters.splice(0)) resolve()
      }
    }
  }
}
